import { useState } from "react";
import PropTypes from "prop-types";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { downloadExcel, downloadPDF } from "utils/exportUtils";

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

// Exports every record matching the table's current filters (all pages), not just the
// rows on screen — the server's /export endpoint does the filtering.
function ExportMenu({ fetchAll, toRows, filename, title, params, filtered, disabled }) {
  const [anchor, setAnchor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const run = async (format) => {
    setAnchor(null);
    setBusy(true);
    setMessage("");
    try {
      const res = await fetchAll(params);
      const rows = toRows(res.items || []);
      const name = `${filename}-${todayYmd()}${filtered ? "-filtered" : ""}`;
      if (format === "excel") downloadExcel(rows, name);
      else downloadPDF(rows, name, title);
      if (res.truncated) {
        setMessage(
          `Export limited to the first ${rows.length.toLocaleString()} of ${res.total.toLocaleString()} matching records.`
        );
      }
    } catch (err) {
      setMessage(err.response?.data?.error || "Export failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <MDBox display="flex" flexWrap="wrap" justifyContent="flex-end" alignItems="center" gap={1}>
      {message && (
        <MDTypography variant="caption" color="warning">
          {message}
        </MDTypography>
      )}
      <MDButton
        size="small"
        variant="gradient"
        color="info"
        // Phones get the regular 40px button height (theme small = 2rem); desktop keeps the small size.
        sx={{ minHeight: { xs: "2.5rem", md: "2rem" } }}
        disabled={disabled || busy}
        onClick={(e) => setAnchor(e.currentTarget)}
        startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <Icon>download</Icon>}
      >
        {busy ? "Exporting…" : "Export"}
      </MDButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem onClick={() => run("excel")}>
          <Icon sx={{ mr: 1 }}>table_view</Icon>Excel (.xlsx)
        </MenuItem>
        <MenuItem onClick={() => run("pdf")}>
          <Icon sx={{ mr: 1 }}>picture_as_pdf</Icon>PDF
        </MenuItem>
      </Menu>
    </MDBox>
  );
}

ExportMenu.defaultProps = { params: {}, filtered: false, disabled: false };

ExportMenu.propTypes = {
  fetchAll: PropTypes.func.isRequired,
  toRows: PropTypes.func.isRequired,
  filename: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  params: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  filtered: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default ExportMenu;
